<div class="parking-zone-details-container p-6 max-w-7xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
            <button mat-icon-button (click)="goBack()" class="back-button">
                <mat-icon>arrow_back</mat-icon>
            </button>
            <div>
                <h1 class="text-3xl font-bold text-gray-900">{{ parkingZone()?.name || ('zoneDetails.titleFallback' |
                    translate) }}</h1>
                <p class="text-sm text-gray-600 mt-1">{{ parkingZone()?.description }}</p>
            </div>
        </div>
        <div class="flex gap-2">
            @if (isAdmin()) {
            <jjames-button [variant]="'secondary'" (clicked)="editParkingZone()" matIconLeft="edit">{{
                'zoneDetails.editZone' | translate }}</jjames-button>
            }
        </div>
    </div>

    @if (isLoading()) {
    <div class="flex items-center justify-center py-12">
        <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
    </div>
    } @else if (parkingZone()) {

    <!-- ✅ QUICK ACTIONS PANEL (Hotline Simple Mode) -->
    <jjames-card class="mb-6 border-l-4 border-l-orange-400">
        <div class="p-6">
            <div class="flex items-center gap-2 mb-4">
                <mat-icon class="text-orange-500">flash_on</mat-icon>
                <h2 class="text-xl font-semibold text-orange-700">{{ 'zoneDetails.quickActions' | translate }}</h2>
                <span class="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                    {{ 'zoneDetails.hotlineMode' | translate }}
                </span>
            </div>

            <!-- Barrier Controls per Device Group -->
            @if (deviceGroupsWithBarriers().length > 0) {
            <div class="space-y-4">
                @for (groupWithBarriers of deviceGroupsWithBarriers(); track groupWithBarriers.deviceGroup.id) {
                <div class="bg-gray-50 rounded-lg p-4">
                    <div class="flex items-center gap-2 mb-3">
                        <mat-icon class="text-gray-600 text-sm">developer_board</mat-icon>
                        <span class="font-medium text-gray-800">{{ groupWithBarriers.deviceGroup.groupName }}</span>
                        <span class="text-xs text-gray-500">({{ groupWithBarriers.barriers.length }} {{
                            'zoneDetails.barriers' | translate }})</span>
                    </div>
                    <div class="flex flex-wrap gap-4">
                        @for (barrier of groupWithBarriers.barriers; track barrier.id) {
                        <div class="flex flex-col gap-1">
                            <span class="text-xs text-gray-500 font-medium">{{ barrier.deviceName ||
                                ('zoneDetails.barrier' | translate) }}</span>
                            <app-zone-barrier-commands [barrierId]="barrier.id!">
                            </app-zone-barrier-commands>
                        </div>
                        }
                    </div>
                </div>
                }
            </div>
            } @else {
            <p class="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                <mat-icon class="text-gray-400 align-middle mr-1" style="font-size:16px">info</mat-icon>
                {{ 'zoneDetails.noBarriers' | translate }}
            </p>
            }
        </div>
    </jjames-card>
    <!-- END QUICK ACTIONS -->

    <!-- Basic Information -->
    <jjames-card class="mb-6">
        <div class="p-6">
            <h2 class="text-xl font-semibold mb-4">{{ 'zoneDetails.basicInfo' | translate }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.governanceType' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.governanceType || 'N/A' }}</p>
                </div>
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.capacity' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.capacity || 'N/A' }}</p>
                </div>
                @if (parkingZone()?.parkingLots && parkingZone()?.parkingLots!.length > 0) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.parkingLots' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.parkingLots!.length }}</p>
                </div>
                }
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.createdAt' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.createdAt ? (parkingZone()?.createdAt | date:'medium') :
                        'N/A' }}</p>
                </div>
                @if (parkingZone()?.updatedAt) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.updatedAt' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.updatedAt | date:'medium' }}</p>
                </div>
                }
            </div>
        </div>
    </jjames-card>

    <!-- Address Information -->
    @if (parkingZone()?.address) {
    <jjames-card class="mb-6">
        <div class="p-6">
            <h2 class="text-xl font-semibold mb-4">{{ 'zoneDetails.address' | translate }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                @if (parkingZone()?.address?.firstAddressLine) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.addressLine1' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.address?.firstAddressLine }}</p>
                </div>
                }
                @if (parkingZone()?.address?.secondAddressLine) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.addressLine2' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.address?.secondAddressLine }}</p>
                </div>
                }
                @if (parkingZone()?.address?.locality) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.locality' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.address?.locality }}</p>
                </div>
                }
                @if (parkingZone()?.address?.postCode) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.postCode' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.address?.postCode }}</p>
                </div>
                }
                @if (parkingZone()?.address?.region) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.region' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.address?.region }}</p>
                </div>
                }
                @if (parkingZone()?.address?.countryCode) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.country' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.address?.countryCode }}</p>
                </div>
                }
            </div>
        </div>
    </jjames-card>
    }

    <!-- Infrastructure Configuration -->
    @if(isAdmin()) {
    <jjames-card class="mb-6">
        <div class="p-6">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-xl font-semibold">{{ 'zoneDetails.infrastructure' | translate }}</h2>
                <jjames-button [variant]="'primary'" (clicked)="configureInfrastructure()" matIconLeft="settings">
                    {{ infrastructure() ? ('zoneDetails.configure' | translate) : ('zoneDetails.addInfrastructure' |
                    translate) }}
                </jjames-button>
            </div>

            @if (infrastructure()) {
            <div class="space-y-4">
                <!-- Device Groups List -->
                @if (infrastructure()?.deviceGroups && infrastructure()!.deviceGroups!.length > 0) {
                <div>
                    <h3 class="text-md font-semibold mb-3">{{ 'zoneDetails.deviceGroups' | translate }} ({{
                        infrastructure()?.deviceGroups?.length }})</h3>
                    <div class="space-y-2">
                        @for (deviceGroup of infrastructure()!.deviceGroups!; track deviceGroup.id) {
                        <div class="border rounded-lg p-4 hover:border-blue-300 transition-colors">
                            <div class="flex items-center justify-between">
                                <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-1">
                                        <mat-icon class="text-blue-600">developer_board</mat-icon>
                                        <p class="font-medium text-lg">{{ deviceGroup.groupName }}</p>
                                    </div>
                                    <div class="grid grid-cols-2 gap-2 text-sm ml-8">
                                        <div>
                                            <span class="text-gray-600">{{ 'zoneDetails.type' | translate }}:</span>
                                            <span class="ml-1 font-medium">{{ deviceGroup.groupType }}</span>
                                        </div>
                                        <div>
                                            <span class="text-gray-600">{{ 'zoneDetails.cameras' | translate }}:</span>
                                            <span class="ml-1 font-medium">{{ deviceGroup.cameras?.length || 0 }}</span>
                                        </div>
                                        @if (deviceGroup.groupDescription) {
                                        <div class="col-span-2">
                                            <span class="text-gray-600">{{ 'zoneDetails.description' | translate
                                                }}:</span>
                                            <span class="ml-1">{{ deviceGroup.groupDescription }}</span>
                                        </div>
                                        }
                                    </div>
                                </div>
                                <div>
                                    <jjames-button [variant]="'primary'" matIconLeft="settings"
                                        (clicked)="configureDeviceGroup(deviceGroup)">
                                        {{ 'zoneDetails.configure' | translate }}
                                    </jjames-button>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                </div>
                } @else {
                <div class="text-center py-6 bg-gray-50 rounded-lg">
                    <mat-icon class="text-gray-400 mb-2">device_hub</mat-icon>
                    <p class="text-gray-600 text-sm">{{ 'zoneDetails.noDeviceGroups' | translate }}</p>
                    <p class="text-gray-500 text-xs mt-1">{{ 'zoneDetails.noDeviceGroupsHint' | translate }}</p>
                </div>
                }

                <div class="flex items-center space-x-2 pt-4 border-t">
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.status' | translate }}</label>
                    <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <mat-icon class="mr-1 text-sm">check_circle</mat-icon>
                        {{ 'zoneDetails.active' | translate }}
                    </span>
                </div>
            </div>
            } @else {
            <div class="text-center py-8 bg-gray-50 rounded-lg">
                <mat-icon class="text-gray-400 mb-2"
                    style="font-size: 48px; width: 48px; height: 48px;">developer_board</mat-icon>
                <p class="text-gray-600 font-medium">{{ 'zoneDetails.noInfrastructure' | translate }}</p>
                <p class="text-gray-500 text-sm mt-1">{{ 'zoneDetails.noInfrastructureHint' | translate }}</p>
            </div>
            }
        </div>
    </jjames-card>
    }
    <!-- Entry Dimensions -->
    @if (parkingZone()?.parkingZoneEntryDimensionDto) {
    <jjames-card class="mb-6">
        <div class="p-6">
            <h2 class="text-xl font-semibold mb-4">{{ 'zoneDetails.entryDimensions' | translate }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                @if (parkingZone()?.parkingZoneEntryDimensionDto?.height) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.height' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.parkingZoneEntryDimensionDto?.height }} {{
                        'zoneDetails.meters' | translate }}</p>
                </div>
                }
                @if (parkingZone()?.parkingZoneEntryDimensionDto?.width) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.width' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.parkingZoneEntryDimensionDto?.width }} {{
                        'zoneDetails.meters' | translate }}</p>
                </div>
                }
                @if (parkingZone()?.parkingZoneEntryDimensionDto?.length) {
                <div>
                    <label class="text-sm text-gray-600">{{ 'zoneDetails.length' | translate }}</label>
                    <p class="font-medium">{{ parkingZone()?.parkingZoneEntryDimensionDto?.length }} {{
                        'zoneDetails.meters' | translate }}</p>
                </div>
                }
            </div>
        </div>
    </jjames-card>
    }

    <!-- Parking Processes Section -->
    @if (zoneId) {
    <app-parking-zone-parking-process [zoneId]="zoneId" [zoneCapacity]="parkingZone()?.capacity || 0"
        class="block mb-6">
    </app-parking-zone-parking-process>
    }
    }
</div>